import {
	Box,
	Button,
	IconButton,
	Dialog, // Changed from Modal to Dialog
	DialogTitle, // Added DialogTitle
	DialogContent, // Added DialogContent
	DialogActions, // Added DialogActions
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import "./ProjectionEditModal.css";
import { useEffect, useState } from "react";
import moment from "moment";
import { EditRounded } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { endOfMonth, startOfMonth } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import {
	CustomDatagridPagination,
	getDefaultFooterStyles,
} from "components/ReusableComponents/CustomDatagridPagination/CustomDatagridPagination";
import {
	aopDefRenderCellStyle,
	aopMrpToCogsDefaultData,
	brandColumn,
	channelColumn,
	daysLeftColumn,
	defRenderCell,
	defRenderHeader,
	StandardRenderCellWithChildren,
} from "../data";
import { converAttrToDatagridCol, createdByColumns, inputTypes, selectOptKeyName } from "data/globalData";
import { nsvTableColumns } from "../Tables/NsvTable/data";
import { gpWithChMarginTableColumns } from "../Tables/GpTable/data";
import { GlobalState } from "context/GlobalContext";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { getDaysLeftInMonth } from "utils/customDateFunctions";
import {
	defSqlRowData,
	fetchAOPPageDataFromScratch,
	generateAopDataCacheKey,
	handleProjectionModalEdirRow,
	handleProjectionModalEditRow,
	mrpToCogsRatioColumn,
	prepareAllAopTableDataBasedOnCombinedData,
	projectionEditModalTableColumns,
	projectionTaxColumn,
} from "./data";
import RenderInputField from "components/Reports/ReusableComponents/RenderInputField/RenderInputField";
import axios from "axios";
import { AuthState } from "context/AuthContext";
import { projectionTableColumns } from "../Tables/ProjectionTable/data";

const modalStyle = {
	// These styles will now be applied to the Paper component within the Dialog
	// width: 800,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: "5px 10px",
	borderRadius: 1,
	minHeight: "50vh",
};

const ProjectionEditModal = ({ modalOpen, setModalOpen, dataArr, setDataArr, saveCallback = () => {} }) => {
	const { setSnackBarOpen, setSnackBar, snackBar, setLoadingOpen, getAppCache, addToAppCache } = GlobalState();
	const { user } = AuthState();
	const [selectedDate, setSelectedDate] = useState(dayjs().startOf("month"));
	const [pageData, setPageData] = useState([]);
	const [tableData, setTableData] = useState([]);
	const [daysLeft, setDaysLeft] = useState(0);
	const [distinctBrands, setDistinctBrands] = useState([]);
	const defInputFieldData = {
		[projectionTaxColumn.key]: projectionTaxColumn.default_value,
		[mrpToCogsRatioColumn.key]: mrpToCogsRatioColumn.default_value,
	};
	const [inputFieldData, setInputFieldData] = useState({ ...defInputFieldData });
	const [isDataUpdated, setIsDataUpdated] = useState(false);

	// Define the desired footer height and background color for this modal's DataGrid
	const footerHeight = 40; // You can change this value
	const footerBackgroundColor = grey[100]; // You can change this value

	// Get the dynamic footer styles using the helper function
	const dynamicFooterStyles = getDefaultFooterStyles(footerHeight, footerBackgroundColor);

	// Props to pass to CustomDatagridPagination
	const customPaginationProps = {
		paginationHeight: footerHeight, // Ensure pagination component matches footer height
		paginationBgColor: footerBackgroundColor, // Ensure pagination component matches footer background
	};

	const tableColumns = [
		{ ...brandColumn, input_type: inputTypes.SELECT.key, name: "BRAND" },
		{ ...channelColumn, input_type: inputTypes.SELECT.key, name: "CHANNEL" },
		// ...Object.values(achievedColumns),
		nsvTableColumns.nsv_aop,
		nsvTableColumns.nsv_achieved,
		{
			...daysLeftColumn,
			renderHeader: defRenderHeader,
			renderCell: ({ row, field }) => (
				<StandardRenderCellWithChildren field={field} row={row} sx={aopDefRenderCellStyle}>
					{daysLeft}
				</StandardRenderCellWithChildren>
			),
		},
		{ ...projectionEditModalTableColumns.PROJ_NSV, name: "PROJ NSV (CR.)" },
		{ ...projectionEditModalTableColumns.PROJ_DIS, name: "PROJ DIS (%)" },
		{ ...gpWithChMarginTableColumns.gp_aop, name: "GP AOP" },
		{ ...gpWithChMarginTableColumns.gp_achieved, name: "GP ACH" },
		// {
		// 	...projectionTableColumns.proj_gp_with_ch_margin,
		// 	name: "Gp With Cm".toUpperCase(),
		// },
		{
			...createdByColumns[1],
			name: createdByColumns[1].name.toUpperCase(),
			renderHeader: defRenderHeader,
			renderCell: defRenderCell,
			headerClassName: "default-header",
			key: "LAST_UPDATED_BY",
		},
	].map((col) => {
		let colObj = { ...col };
		if (colObj.renderCell) {
			const originalRenderCell = colObj.renderCell;
			const valueGetter = colObj.valueGetter ?? function () {};
			colObj.renderCell = (params) => originalRenderCell.call({ valueGetter }, params);
		}

		return { ...colObj, ...converAttrToDatagridCol(colObj), minWidth: 80 };
	});

	const handleCancelClick = () => {
		setTableData(pageData);
		setModalOpen(false);
		setInputFieldData({ ...defInputFieldData });
		setSelectedDate(dayjs().startOf("month"));
	};

	const loadData = async () => {
		const dateRange = {
			startDate: dayjs(selectedDate).startOf("month").format("YYYY-MM-DD"),
			endDate: dayjs(selectedDate).endOf("month").format("YYYY-MM-DD"),
		};
		const cacheKey = generateAopDataCacheKey(dateRange);
		console.log(cacheKey, "looking for cache (Modal)");
		const cached = getAppCache(cacheKey);
		const currentMonth = dayjs().get("M");
		if (cached) {
			const startMonth = dayjs(dateRange.startDate).get("M");
			const endMonth = dayjs(dateRange.endDate).get("M");
			console.log("Start date:", dayjs(dateRange.startDate).format(), "Month:", dayjs(dateRange.startDate).get("M"));
			console.log("End date:", dayjs(dateRange.endDate).format(), "Month:", dayjs(dateRange.endDate).get("M"));
			console.log("Cur Month:", dayjs().format(), "Month:", dayjs().get("M"));
			if (startMonth !== currentMonth && endMonth !== currentMonth) {
				console.log("loaded page data from cache (Modal)");
				return cached;
			}
			// Proceed with fresh logic here
		}
		const data = await fetchAOPPageDataFromScratch(dateRange, addToAppCache, getAppCache);
		addToAppCache(cacheKey, data);
		return data;
	};

	const fetch = async () => {
		try {
			setLoadingOpen(true);
			const { filterDropdowns: filters, fullData } = await loadData();
			setPageData(fullData);
			const { brandTableData, channelTableData, combinedTableData } = prepareAllAopTableDataBasedOnCombinedData(fullData);
			setTableData(combinedTableData);
			const distinctBrands = brandTableData
				?.filter((row) => row?.[brandColumn.key] !== "THOR")
				.map((row) => row?.[brandColumn.key]);
			setDistinctBrands(distinctBrands);
			setInputFieldData({
				...inputFieldData,
				[projectionTaxColumn.key]: fullData?.[0]?.[projectionTaxColumn.key] ?? 0,
				...distinctBrands.reduce((acc, brandStr) => {
					let filter = combinedTableData.filter((row) => row?.[brandColumn.key] === brandStr);
					if (filter[0])
						if (filter[0]?.[mrpToCogsRatioColumn.key]) acc[brandStr] = filter[0]?.[mrpToCogsRatioColumn.key];
						else acc[brandStr] = aopMrpToCogsDefaultData[brandStr] ?? 0;

					return acc;
				}, {}),
			});
			setLoadingOpen(false);
		} catch (error) {
			console.log(error);
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		if (modalOpen) {
			fetch();
		}
		setDaysLeft(getDaysLeftInMonth(selectedDate));
	}, [selectedDate, modalOpen]);

	useEffect(() => {
		if (!pageData?.[0]?.[projectionTaxColumn.key] || !pageData?.[0]?.[mrpToCogsRatioColumn.key]) setIsDataUpdated(true);
	}, [inputFieldData]);
	const handleRowDataChange = (updatedRow) => {
		const updatedData = handleProjectionModalEditRow(updatedRow);
		if (updatedData) {
			setIsDataUpdated(true);
			setTableData((prev) =>
				prev?.map((row) => {
					if (row?.BRAND === updatedRow.BRAND && row.CHANNEL === updatedRow.CHANNEL)
						return {
							...row,
							...updatedData,
							LAST_UPDATED_BY: user.email,
						};
					else return row;
				})
			);
			return updatedData;
		}
	};

	const onBrandWiseMrpCogsDataCallback = (e, value, fullData, column) => {
		let arr = tableData?.map((row) => {
			if (row[brandColumn.key] === column.key) {
				row[mrpToCogsRatioColumn.key] = value;
				row["LAST_UPDATED_BY"] = user.email;
			}
			return row;
		});
		setTableData(arr);
	};

	const handleSaveClick = async () => {
		setLoadingOpen(true);
		if (inputFieldData[projectionTaxColumn.key] > 0) {
			const finalTableData = tableData?.reduce(
				(acc, row) => {
					let obj = {
						...Object.keys(defSqlRowData).reduce((acc, key) => {
							acc[key] = row[key] ? row[key] : defSqlRowData[key];
							return acc;
						}, {}),
						[brandColumn.key]: row[brandColumn.key],
						[channelColumn.key]: row[channelColumn.key],
						MONTH_YEAR: dayjs(selectedDate).format("YYYY-MM-DD"),
						[projectionTaxColumn.key]: parseFloat(inputFieldData?.[projectionTaxColumn.key])
							? parseFloat(inputFieldData?.[projectionTaxColumn.key])
							: 0,
						[mrpToCogsRatioColumn.key]: parseFloat(row?.[mrpToCogsRatioColumn.key])
							? parseFloat(row?.[mrpToCogsRatioColumn.key])
							: 0,
						UPDATED_AT: new Date(),
					};
					acc.projData.push(obj);
					acc.fullData.push({ row, ...obj });
					return acc;
				},
				{ fullData: [], projData: [] }
			);
			try {
				await axios.post("/api/aop/create_or_update_projection", finalTableData.projData).then((res) => {
					const dateRange = {
						startDate: dayjs(selectedDate).startOf("month").format(),
						endDate: dayjs(selectedDate).endOf("month").format(),
					};
					const cacheKey = generateAopDataCacheKey(dateRange);
					// addToAppCache(cacheKey, finalTableData.fullData);
					saveCallback(finalTableData.fullData);
					setLoadingOpen(false);
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						message: "Projection Saved Successfully",
						severity: "success",
						anchorOrigin: { horizontal: "center", vertical: "top" },
					});
					setModalOpen(false); // Close modal after saving
				});
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: parseErrMsgFromError(error),
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			}
		} else {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please enter Projection Tax %",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	return (
		<Dialog
			open={modalOpen}
			// onClose={() => setModalOpen(false)}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: modalStyle,
			}}>
			<Box display="flex" justifyContent="space-between" width="100%" alignItems="center" columnGap="10px">
				<DialogTitle height={"25px"}>Edit Projection Details</DialogTitle>
				<Box pr={3}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							views={["year", "month"]}
							value={selectedDate}
							disabled={isDataUpdated}
							onChange={(newValue) => setSelectedDate(newValue)}
							renderInput={(params) => <TextField {...params} fullWidth sx={{ mt: 1 }} />}
							slotProps={{
								popper: {
									sx: {
										maxHeight: "150px",
										"& .MuiYearCalendar-root": {
											maxHeight: "150px",
											overflowY: "auto",
										},
										"& .MuiPaper-root": {
											maxHeight: "220px",
										},
									},
								},
								textField: {
									size: "small",
									sx: {},
								},
							}}
						/>
					</LocalizationProvider>
				</Box>
			</Box>

			<DialogContent dividers sx={{ pt: 1 }}>
				<Box width="100% " display="flex" justifyContent="flex-end" pb={1} alignItems="center" columnGap="10px">
					{distinctBrands?.map((brand) => {
						return (
							<Box display="flex" alignItems="center" justifyContent="center">
								<Typography fontSize={13} textAlign="center">
									{mrpToCogsRatioColumn.name} ({brand})
								</Typography>
								<RenderInputField
									column={{ ...mrpToCogsRatioColumn, name: brand, key: brand }}
									data={inputFieldData}
									setData={setInputFieldData}
									selectOptObjName={selectOptKeyName}
									width={"100px"}
									longWidth={"300px"}
									disabled={false}
									disableLabel={true}
									onChangeCallBack={onBrandWiseMrpCogsDataCallback}
								/>
							</Box>
						);
					})}

					<Box display="flex" alignItems="center">
						<Typography fontSize={13}>{projectionTaxColumn.name}</Typography>
						<RenderInputField
							column={projectionTaxColumn}
							data={inputFieldData}
							setData={setInputFieldData}
							selectOptObjName={selectOptKeyName}
							width={"100px"}
							longWidth={"300px"}
							disabled={false}
							disableLabel={true}
						/>
					</Box>
				</Box>
				<DataGrid
					rows={tableData}
					columns={tableColumns}
					getRowId={(row) => row.CHANNEL + row.BRAND}
					showCellVerticalBorder
					sx={{
						minHeight: 120, // Keep minHeight for the grid content
						width: "100%",
						border: "none",
						"& .MuiDataGrid-row": {
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-cell": {
							padding: 0,
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							padding: 0,
							width: "100%",
							height: "100%",
						},
						...dynamicFooterStyles, // Apply the dynamic footer styles here
					}}
					columnHeaderHeight={35}
					rowHeight={30}
					processRowUpdate={handleRowDataChange}
					pagination
					slots={{
						// toolbar: GridToolbar,
						pagination: (props) => <CustomDatagridPagination {...props} {...customPaginationProps} />,
					}}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 8 },
						},
					}}
					disableColumnFilter={false}
					disableRowSelectionOnClick
					pageSizeOptions={[15]}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancelClick} variant="outlined" sx={{ mr: 1 }} size="small">
					Cancel
				</Button>
				<Button onClick={handleSaveClick} variant="contained" size="small" disabled={!isDataUpdated}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProjectionEditModal;
